'use client';

import { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import Icon from '@/components/ui/AppIcon';

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

const MobileProcessTimeline = () => {
    const containerRef = useRef<HTMLElement>(null);
    const activePathRef = useRef<SVGPathElement>(null);

    useGSAP(() => {
        const path = activePathRef.current;
        if (!path) return;

        // Path Length Calculation
        const length = path.getTotalLength();

        // Safety check
        if (!length) return;

        // "Snake" Logic: A traveling dash
        // We want a segment that is, say, 35% of the total path length
        const snakeLength = length * 0.35;

        // Initial State: Dash pattern
        // strokeDasharray = "dashLength gapLength"
        // We set gap to HUGE (4x length) so no second snake ever appears
        gsap.set(path, {
            strokeDasharray: `${snakeLength} ${length * 4}`,
            strokeDashoffset: snakeLength, // Start hidden
            autoAlpha: 1
        });

        // Animate the snake traveling down the path
        gsap.fromTo(path,
            { strokeDashoffset: snakeLength },
            {
                // Move it WAY past the end to ensure it completely clears the view
                strokeDashoffset: -(length * 3),
                ease: "none",
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: "top 30%",
                    end: "bottom 40%",
                    scrub: 1,
                }
            }
        );

        // Animate text cards fading in
        const steps = gsap.utils.toArray<HTMLElement>('.mobile-process-step');
        steps.forEach((step, i) => {
            gsap.fromTo(step,
                { opacity: 0, y: 30 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    scrollTrigger: {
                        trigger: step,
                        start: "top 80%",
                        toggleActions: "play none none reverse",
                    }
                }
            );
        });

    }, { scope: containerRef });

    const steps = [
        {
            id: 1,
            title: "Discovery",
            description: "We dive deep into your business goals, architectural needs, and market position.",
            icon: "MagnifyingGlassIcon",
            align: "left"
        },
        {
            id: 2,
            title: "Strategy",
            description: "Developing a roadmap that combines technical scalability with SEO dominance.",
            icon: "MapIcon",
            align: "right"
        },
        {
            id: 3,
            title: "Development",
            description: "Agile build cycles with constant feedback, ensuring pixel-perfect implementation.",
            icon: "CodeBracketSquareIcon",
            align: "left"
        },
        {
            id: 4,
            title: "Launch & Scale",
            description: "Deployment to edge networks followed by aggressive visibility campaigns.",
            icon: "RocketLaunchIcon",
            align: "right"
        }
    ];

    return (
        <section ref={containerRef} className="py-20 bg-surface relative overflow-hidden">

            {/* Header */}
            <div className="text-center mb-16 px-6 relative z-10">
                <h2 className="text-3xl font-black text-slate-900 mb-3">The Journey</h2>
                <p className="text-slate-600 font-medium">From concept to dominance.</p>
            </div>

            {/* Background Snake Path Layer (Z-0) */}
            <div className="absolute inset-0 w-full h-full pointer-events-none z-0 opacity-20">
                <svg className="w-full h-full" viewBox="0 0 100 1000" preserveAspectRatio="none">
                    {/* 
                        Vertical Snake Path Construction:
                        Starts Top Center (50, 0)
                        - Curves Left to Step 1
                        - Curves Right to Step 2
                        - Curves Left to Step 3
                        - Curves Right to Step 4
                        Measurements approximate to match card positions.
                     */}
                    <path
                        className="mobile-track-path"
                        d="M 50 0 
                           C 50 100, 10 150, 10 250 
                           C 10 350, 90 400, 90 500 
                           C 90 600, 10 650, 10 750 
                           C 10 850, 50 900, 50 1000"
                        fill="none"
                        stroke="#94a3b8"
                        strokeWidth="2"
                        vectorEffect="non-scaling-stroke"
                    />
                    <path
                        ref={activePathRef}
                        id="mobile-process-path"
                        d="M 50 0 
                           C 50 100, 10 150, 10 250 
                           C 10 350, 90 400, 90 500 
                           C 90 600, 10 650, 10 750 
                           C 10 850, 50 900, 50 1000"
                        fill="none"
                        stroke="black"
                        strokeWidth="3"
                        vectorEffect="non-scaling-stroke"
                        strokeLinecap="round"
                    />
                </svg>
            </div>

            {/* Steps Container (Z-10) */}
            <div className="relative z-10 px-6 space-y-24 mt-12">
                {steps.map((step, index) => (
                    <div
                        key={step.id}
                        className={`mobile-process-step flex flex-col ${step.align === 'right' ? 'items-end text-right' : 'items-start text-left'}`}
                    >
                        {/* Clean Text Container - No Box */}
                        <div className="relative max-w-[85%] z-20">
                            <div className={`flex flex-col ${step.align === 'right' ? 'items-end' : 'items-start'}`}>
                                {/* Icon */}
                                <div className="mobile-icon-container mb-3 p-3 rounded-xl bg-slate-100 text-slate-900 shadow-sm inline-flex transition-all">
                                    <Icon name={step.icon} className="w-6 h-6" />
                                </div>

                                <h3 className="text-2xl font-black text-slate-900 mb-2">{step.title}</h3>
                                <p className="text-base text-slate-600 font-medium leading-relaxed">{step.description}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

        </section>
    );
};

export default MobileProcessTimeline;
